/**
 * Shared Axios instance with CSRF interceptor.
 * Framework-agnostic — usable by both Vue and React.
 */
import axios from "axios";
import Flash from "./flash";

const api = axios.create();

// Set common headers
api.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

// Initialize CSRF token from meta tag
const csrfMeta = document.head.querySelector("[name=csrf-token]");
if (csrfMeta) {
    api.defaults.headers.common["X-CSRF-Token"] = csrfMeta.content;
}

// Fetch a fresh CSRF token: a CFWheels reload rotates the cookie-encryption secret, so an
// open session's token goes stale. GETting the app root re-issues the csrftoken cookie and
// renders a matching token in the <meta>; parse and return it.
async function fetchFreshCsrfToken() {
    try {
        const res = await fetch("/", {
            headers: { "X-Requested-With": "XMLHttpRequest" },
            credentials: "same-origin",
        });
        const html = await res.text();
        const m = html.match(/<meta[^>]+name=["']csrf-token["'][^>]+content=["']([^"']+)["']/i);
        return m ? m[1] : null;
    } catch {
        return null;
    }
}

function applyCsrfToken(token) {
    api.defaults.headers.common["X-CSRF-Token"] = token;
    const meta = document.head.querySelector("[name=csrf-token]");
    if (meta) meta.content = token;
}

// CSRF auto-refresh interceptor.
//
// Two failure shapes, both from a stale token after a CFWheels reload:
//   * Backend that implements protectsFromForgery(with="custom400"): HTTP 400 + a fresh
//     X-CSRF-Token response header — swap it in and retry (the designed path).
//   * Backend that still renders the CSRF error page: HTTP 200 with a text/html body — axios
//     treats it as success, so the form saw an unparseable "success" and showed
//     "server returned an unexpected response". Detect the HTML body on a mutating request,
//     fetch a fresh token, and retry once.
api.interceptors.response.use(
    async function (response) {
        const cfg = response.config || {};
        const method = (cfg.method || "get").toLowerCase();
        const isMutating = method === "post" || method === "put" || method === "patch" || method === "delete";
        const ctype = String(response.headers?.["content-type"] || "").toLowerCase();
        const looksHtml =
            ctype.includes("text/html") ||
            (typeof response.data === "string" && /^\s*<(!doctype|html)/i.test(response.data));
        if (isMutating && looksHtml && !cfg.__csrfRetried) {
            cfg.__csrfRetried = true;
            const token = await fetchFreshCsrfToken();
            if (token) {
                applyCsrfToken(token);
                cfg.headers = cfg.headers || {};
                cfg.headers["X-CSRF-Token"] = token;
                return api.request(cfg);
            }
            Flash.error("Your session was refreshed. Please try saving again.");
        }
        return response;
    },
    function (error) {
        const response = error.response;
        const status = response ? response.status : null;
        const originalRequest = error.config;

        if (status === 400) {
            if (response.headers && response.headers["x-csrf-token"]) {
                let newToken = response.headers["x-csrf-token"];
                applyCsrfToken(newToken);
                originalRequest.headers["X-CSRF-Token"] = newToken;
                return api.request(originalRequest).then((response) => response);
            } else {
                Flash.error(
                    "The application has been restarted by an administrator. Please refresh your browser."
                );
            }
        }
        return Promise.reject(error);
    }
);

export default api;
